import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Container, Paper, Typography, CircularProgress, Button } from '@mui/material';
import { CheckCircleOutline, ErrorOutline, InfoOutlined } from '@mui/icons-material';
import axios from 'axios';
import { colors } from '../theme/modernTheme';
import { API_BASE_URL } from '../services/api';

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading, success, already_unsubscribed, error, resubscribed
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isResubscribing, setIsResubscribing] = useState(false);

  useEffect(() => {
    const processUnsubscribe = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid unsubscribe link. No token provided.');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/unsubscribe`, {
          params: { token },
        });

        if (response.data.success) {
          const isAlreadyUnsubscribed = response.data.message.includes('already unsubscribed');
          setStatus(isAlreadyUnsubscribed ? 'already_unsubscribed' : 'success');
          setMessage(response.data.message);
          setEmail(response.data.contact?.email || '');
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Failed to unsubscribe');
        }
      } catch (error) {
        setStatus('error');
        if (error.response?.data?.message) {
          setMessage(error.response.data.message);
        } else if (error.response?.status === 400) {
          setMessage('Invalid or expired unsubscribe link');
        } else {
          setMessage('An error occurred while processing your request');
        }
      }
    };

    processUnsubscribe();
  }, [token]);

  const handleResubscribe = async () => {
    setIsResubscribing(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/unsubscribe/resubscribe`, {
        params: { token },
      });

      if (response.data.success) {
        setStatus('resubscribed');
        setMessage(response.data.message);
        setEmail(response.data.contact?.email || '');
      } else {
        setStatus('error');
        setMessage(response.data.message || 'Failed to resubscribe');
      }
    } catch (error) {
      setStatus('error');
      if (error.response?.data?.message) {
        setMessage(error.response.data.message);
      } else {
        setMessage('An error occurred while processing your request');
      }
    } finally {
      setIsResubscribing(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Processing your request...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we unsubscribe you from our emails.
            </Typography>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircleOutline sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
              Successfully Unsubscribed
            </Typography>
            <Typography variant="body1" color="text.primary" sx={{ mb: 1 }}>
              {message}
            </Typography>
            {email && (
              <Typography variant="body2" color="text.primary" sx={{ mb: 3 }}>
                Email: <strong>{email}</strong>
              </Typography>
            )}
            <Typography variant="body2" color="text.primary" sx={{ mt: 3, mb: 3 }}>
              You will no longer receive emails from us.
            </Typography>
            <Button
              variant="outlined"
              onClick={handleResubscribe}
              disabled={isResubscribing}
              sx={{
                mt: 2,
                borderRadius: '4px',
                color: colors.primary.main,
                '&:hover': {
                  borderColor: 'inherit',
                  color: colors.primary.dark,
                },
              }}
            >
              {isResubscribing ? 'Processing...' : 'Resubscribe'}
            </Button>
          </Box>
        );

      case 'already_unsubscribed':
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <InfoOutlined sx={{ fontSize: 80, color: 'info.main', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: colors.text.dark }}>
              Already Unsubscribed
            </Typography>
            <Typography variant="body1" color="text.primary" sx={{ mb: 1 }}>
              {message}
            </Typography>
            {email && (
              <Typography variant="body2" color="text.primary" sx={{ mb: 3 }}>
                Email: <strong>{email}</strong>
              </Typography>
            )}
            <Typography variant="body2" color="text.primary" sx={{ mt: 3, mb: 3 }}>
              You are not receiving emails from us.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleResubscribe}
              disabled={isResubscribing}
              sx={{
                mt: 2,
                borderRadius: '4px',
                color: colors.primary.main,
                '&:hover': {
                  borderColor: 'inherit',
                  color: colors.primary.dark,
                },
              }}
            >
              {isResubscribing ? 'Processing...' : 'Resubscribe'}
            </Button>
          </Box>
        );

      case 'resubscribed':
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircleOutline sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
              Successfully Resubscribed
            </Typography>
            <Typography variant="body1" color="text.primary" sx={{ mb: 1 }}>
              {message}
            </Typography>
            {email && (
              <Typography variant="body2" color="text.primary" sx={{ mb: 3 }}>
                Email: <strong>{email}</strong>
              </Typography>
            )}
            <Typography variant="body2" color="text.primary" sx={{ mt: 3 }}>
              You will now receive emails from us again.
            </Typography>
          </Box>
        );

      case 'error':
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ErrorOutline sx={{ fontSize: 80, color: 'error.main', mb: 3 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Unsubscribe Failed
            </Typography>
            <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
              {message}
            </Typography>
            <Typography variant="body2" color="text.primary">
              If you continue to experience issues, please contact support.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: colors.background.default,
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            borderRadius: '4px',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 4 }}>{renderContent()}</Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default UnsubscribePage;
